import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  Typography,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Tooltip,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { styled, alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { CalendarEventDto } from '@/types/calendar';
import { CalendarEventDetails } from './CalendarEventDetails';
import { EventTypeIcon } from './EventTypeIcon';
import { CalendarEventStatusCard, EventCardStatus } from './CalendarEventStatusCard';

interface Props {
  event: CalendarEventDto;
  /** Задано - картка в новому вигляді `CalendarEventStatusCard` (календар клієнта в зоні експерта). */
  completionStatus?: EventCardStatus;
  calendarName?: string;
  calendarAccentColor?: string;
  currentUserId?: number;
  onClick: () => void;
  onEdit?: (event: CalendarEventDto) => void;
  onDelete?: (event: CalendarEventDto) => void;
}

const POPOVER_WIDTH = 280;
const POPOVER_GAP = 6;

const CardWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '28px',
  marginBottom: '2px',
  '& .quick-delete-btn': {
    opacity: 0,
    transition: 'opacity 0.15s',
  },
  '&:hover .quick-delete-btn': {
    opacity: 1,
  },
});

const TriggerCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accentcolor' && prop !== 'calendarcolor',
})<{ accentcolor?: string; calendarcolor?: string }>(({ theme, accentcolor, calendarcolor }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: '3px 4px',
  minHeight: '28px',
  backgroundColor: calendarcolor
    ? alpha(calendarcolor, 0.08)
    : theme.palette.background.paper,
  borderLeft: `3px solid ${accentcolor || theme.palette.divider}`,
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${calendarcolor ? alpha(calendarcolor, 0.35) : theme.palette.divider}`,
}));

const PopoverCardStyled = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'accentcolor',
})<{ accentcolor?: string }>(({ theme, accentcolor }) => ({
  position: 'fixed',
  zIndex: 1500,
  width: `${POPOVER_WIDTH}px`,
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `3px solid ${accentcolor || theme.palette.divider}`,
  boxShadow: theme.shadows[8],
  cursor: 'pointer',
  pointerEvents: 'auto',
}));

interface PopoverPosition {
  top: number;
  left: number;
}

function usePopoverPosition(
  wrapperRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
): PopoverPosition {
  const [pos, setPos] = useState<PopoverPosition>({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen || !wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Approximate popover height — enough for most cards
    const estimatedHeight = 220;

    // Prefer right side; fall back to left
    let left = rect.right + POPOVER_GAP;
    if (left + POPOVER_WIDTH > vw - 8) {
      left = rect.left - POPOVER_WIDTH - POPOVER_GAP;
    }
    left = Math.max(8, left);

    // Align top with card; shift up if it would overflow bottom
    let top = rect.top;
    if (top + estimatedHeight > vh - 8) {
      top = vh - estimatedHeight - 8;
    }
    top = Math.max(8, top);

    setPos({ top, left });
  }, [isOpen, wrapperRef]);

  return pos;
}

export const CalendarEventCard: React.FC<Props> = ({
  event,
  calendarName,
  calendarAccentColor,
  currentUserId,
  onClick,
  onEdit,
  onDelete,
  completionStatus,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLElement | null>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isTouchDevice = useMediaQuery('(hover: none)');
  const shouldDisablePopover = isSmallScreen || isTouchDevice;

  const popoverPos = usePopoverPosition(wrapperRef, isHovered && !shouldDisablePopover);

  const handleMouseEnter = () => {
    if (shouldDisablePopover) return;
    timerRef.current = setTimeout(() => setIsHovered(true), 150);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsHovered(false);
  };

  const isExpert = !!calendarName;
  const rawInput: string | undefined = (['food', 'workout', 'medication'] as string[]).includes(event.eventType.code)
    ? (event.details as Record<string, unknown>)?.rawInput as string | undefined
    : undefined;

  const popover = isHovered && !shouldDisablePopover
    ? ReactDOM.createPortal(
        <PopoverCardStyled
          accentcolor={calendarAccentColor || event.eventType.color}
          onClick={onClick}
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <CardContent sx={{ p: '12px !important' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                {event.title || event.eventType.name}
              </Typography>
              <Checkbox
                checked={event.isCompleted}
                size="small"
                disableRipple
                onClick={(e) => e.stopPropagation()}
                icon={<RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />}
                checkedIcon={<CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />}
                sx={{ p: 0, ml: 1 }}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {event.scheduledLdt ? format(new Date(event.scheduledLdt), 'dd.MM.yyyy HH:mm') : 'Дата не вказана'}
              </Typography>
              {event.eventType.name !== event.title && (
                <Typography variant="caption" color="text.secondary">
                  Тип: {event.eventType.name}
                </Typography>
              )}
              <CalendarEventDetails event={event} />
              {calendarName && (
                <Chip
                  icon={<PersonIcon sx={{ fontSize: 12 }} />}
                  label={calendarName}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                />
              )}
            </Box>

            {(onEdit || onDelete) && (
              <CardActions sx={{ p: 0, mt: 1.5, justifyContent: 'flex-end' }}>
                {onEdit && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(event); }} sx={{ p: 0.5 }}>
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(event); }} sx={{ p: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </CardActions>
            )}
          </CardContent>
        </PopoverCardStyled>,
        document.body,
      )
    : null;

  return (
    <CardWrapper
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {completionStatus !== undefined ? (
        <CalendarEventStatusCard event={event} status={completionStatus} onClick={onClick} />
      ) : (
        <TriggerCard
          accentcolor={event.eventType.color}
          calendarcolor={calendarAccentColor}
          onClick={onClick}
        >
          <EventTypeIcon
            code={event.eventType.code}
            icon={event.eventType.icon}
            accentColor={event.eventType.color}
            size={16}
            sx={{ flexShrink: 0, alignSelf: rawInput ? 'flex-start' : 'center', mt: rawInput ? '1px' : 0 }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} noWrap sx={{ fontSize: '0.75rem', lineHeight: 1.2, display: 'block' }}>
              {event.title || event.eventType.name}
            </Typography>
            {rawInput && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem', lineHeight: 1.1, display: 'block', opacity: 0.8 }}>
                {rawInput}
              </Typography>
            )}
          </Box>
          {isExpert && currentUserId !== undefined && event.createdBy?.id === currentUserId ? (
            <Tooltip title="Моя подія" placement="top">
              <Avatar
                src={event.createdBy.avatar ?? undefined}
                sx={{ width: 14, height: 14, fontSize: '0.5rem', flexShrink: 0, opacity: 0.85 }}
              >
                {!event.createdBy.avatar && event.createdBy.name.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          ) : isExpert ? (
            <Tooltip title={calendarName} placement="top">
              <SupervisorAccountIcon sx={{ fontSize: 14, flexShrink: 0, opacity: 0.6, color: calendarAccentColor }} />
            </Tooltip>
          ) : null}
        </TriggerCard>
      )}

      {onDelete && (
        <IconButton
          className="quick-delete-btn"
          size="small"
          onClick={e => { e.stopPropagation(); onDelete(event); }}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 2,
            transform: 'translateY(-50%)',
            width: 20,
            height: 20,
            p: 0,
            zIndex: 1,
            bgcolor: 'error.main',
            color: 'common.white',
            '&:hover': { bgcolor: 'error.dark' },
          }}
        >
          <DeleteIcon sx={{ fontSize: 13 }} />
        </IconButton>
      )}

      {popover}
    </CardWrapper>
  );
};
